import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "../lib/api";
import DropLocationPicker from "./DropLocationPicker";
import OfciProjectRow from "./OfciProjectRow";

const errText = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || fb; };
const EMPTY_LOC = { building: "", floor: "", area: "", room_key: "", room_label: "", note: "" };

/** FLD-12 — "Verify OFCI Onsite": the whole project's Expected-Onsite OFCI requirement, whether or
 *  not it is on a picking or delivery ticket. Shared by the full-page Delivery view, Install ›
 *  Material and View All Onsite Material — all three write the same FRS material state.
 *  `ticketId` is recorded on the verification when this happens during a delivery. */
export default function OfciProjectDialog({ projectId, actor, ticketId = "", onClose, onDone }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [open, setOpen] = useState({});
  const [q, setQ] = useState("");
  const [hideDone, setHideDone] = useState(true);
  const [dest, setDest] = useState("location");      // location | assigned
  const [loc, setLoc] = useState(EMPTY_LOC);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data: d } = await api.get("/ofci/project", { params: { project_id: projectId } });
    setData(d);
  }, [projectId]);
  useEffect(() => { load().catch((e) => toast.error(errText(e, "Could not load the OFCI list"))); }, [load]);

  const products = useMemo(() => (data?.products || []).filter((g) =>
    (!hideDone || g.unverified > 0) &&
    (!q || `${g.description} ${g.product_short} ${g.scopes.join(" ")}`.toLowerCase().includes(q.toLowerCase()))), [data, hideDone, q]);
  const patch = (key, next) => setSel((c) => ({ ...c, [key]: { ...(c[key] || {}), ...next } }));
  const chosen = products.filter((g) => sel[g.key]?.picked);
  const units = chosen.reduce((s, g) => s + (sel[g.key]?.notFound ? 0 : Number(sel[g.key]?.qty) || 0), 0);

  const selectAll = () => setSel(Object.fromEntries(products.filter((g) => g.unverified > 0)
    .map((g) => [g.key, { picked: true, qty: g.unverified }])));

  // Every reason this action cannot be saved, shown in the footer and blocking the button —
  // never a silently swallowed click.
  const problems = useMemo(() => {
    const out = [];
    for (const g of chosen) {
      const s = sel[g.key] || {};
      const qty = s.notFound ? 0 : Number(s.qty) || 0;
      const roomTotal = Object.values(s.rooms || {}).reduce((t, v) => t + (Number(v) || 0), 0);
      if (!s.notFound && qty <= 0) out.push(`${g.product_short || g.description}: enter the quantity found, or tick 'not found onsite'`);
      else if (qty > g.unverified + 1e-9) out.push(`${g.product_short || g.description}: only ${g.unverified} are still unverified — lower the quantity or tick 'not found onsite'`);
      else if (roomTotal > 0 && Math.abs(roomTotal - qty) > 1e-9) out.push(`${g.product_short || g.description}: the room quantities add up to ${roomTotal}, not ${qty}`);
    }
    if (chosen.length && dest === "location" && !(loc.note || loc.room_label || loc.building || loc.area)
        && !chosen.every((g) => Object.values(sel[g.key]?.rooms || {}).some((v) => Number(v) > 0)
                                || (sel[g.key]?.notFound))) {
      out.push("Say where you found it, or choose 'spread straight to the assigned rooms'");
    }
    return out;
  }, [chosen, sel, dest, loc]);

  const submit = async () => {
    if (!chosen.length) { toast.info("Tick the OFCI products you found"); return; }
    if (problems.length) { toast.error(problems[0]); return; }
    const items = chosen.map((g) => {
      const s = sel[g.key] || {};
      return {
        key: g.key, qty: s.notFound ? 0 : Number(s.qty) || 0, not_found: !!s.notFound,
        rooms: Object.entries(s.rooms || {}).map(([room_key, v]) => ({ room_key, qty: Number(v) || 0 }))
          .filter((r) => r.qty > 0),
      };
    });
    setBusy(true);
    try {
      const { data: res } = await api.post("/ofci/project/verify", {
        project_id: projectId, by: actor, verified_on: date, note, ticket_id: ticketId || "",
        spread_assigned: dest === "assigned",
        location: { building: loc.building, floor: loc.floor, area: loc.area, room_key: loc.room_key,
                    label: loc.note || loc.room_label || "" },
        items,
      });
      toast.success(res.message);
      setSel({}); setOpen({});
      await load();
      onDone?.(res);
    } catch (e) { toast.error(errText(e, "Could not save the verification")); } finally { setBusy(false); }
  };

  const t = data?.totals || {};
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl p-0 gap-0 max-h-[92vh] flex flex-col" data-testid="ofci-project-dialog">
        <DialogHeader className="px-4 pt-4 pb-2 shrink-0">
          <DialogTitle className="text-sm">Verify OFCI material onsite — whole job</DialogTitle>
          <DialogDescription className="text-xs">
            Every owner-furnished item the FRS expects on site, ticket or no ticket. Verifying records it as onsite
            material — it never becomes ABS warehouse stock, never counts on a delivery and is never marked installed.
            {ticketId ? " This delivery is recorded on the verification." : ""}
          </DialogDescription>
          <div className="flex flex-wrap gap-x-3 text-[12px] tabular-nums pt-1" data-testid="ofci-project-totals">
            <span>FRS required <b>{t.frs_required ?? 0}</b></span>
            <span>Verified onsite <b className="text-emerald-700">{t.verified ?? 0}</b></span>
            <span>Still unverified <b className="text-amber-800">{t.unverified ?? 0}</b></span>
            <span>Installed <b>{t.installed ?? 0}</b></span>
            <span>Onsite, uninstalled <b className="text-emerald-700">{t.onsite_uninstalled ?? 0}</b></span>
          </div>
        </DialogHeader>
        <div className="px-4 pb-2 flex flex-wrap items-center gap-2 shrink-0 border-b border-gray-200">
          <span className="relative flex-1 min-w-[160px]">
            <Search className="w-3.5 h-3.5 absolute left-2 top-3 sm:top-2.5 text-gray-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..."
                   className="pl-7 min-h-[40px] sm:min-h-[34px] text-[13px]" data-testid="ofci-project-search" />
          </span>
          <label className="flex items-center gap-1.5 text-[12px]">
            <input type="checkbox" checked={hideDone} onChange={(e) => setHideDone(e.target.checked)}
                   className="w-4 h-4" data-testid="ofci-project-hide-done" />
            Hide fully verified
          </label>
          <button type="button" onClick={selectAll} className="text-[12px] underline text-blue-700" data-testid="ofci-project-select-all">
            Select all ({products.filter((g) => g.unverified > 0).length})
          </button>
          <button type="button" onClick={() => setSel({})} className="text-[12px] underline text-gray-500" data-testid="ofci-project-clear">Clear</button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {!data ? <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
            <ul className="divide-y divide-gray-200">
              {products.length === 0 && (
                <li className="p-6 text-center text-sm text-gray-500" data-testid="ofci-project-empty">
                  No Expected-Onsite OFCI material is outstanding on this job.
                </li>
              )}
              {products.map((g) => (
                <OfciProjectRow key={g.key} g={g} state={sel[g.key]} open={!!open[g.key]}
                                onToggleOpen={() => setOpen((c) => ({ ...c, [g.key]: !c[g.key] }))}
                                setState={(next) => patch(g.key, next)} />
              ))}
            </ul>
          )}
        </div>
        <div className="px-4 py-2 border-t border-gray-200 space-y-2 shrink-0" data-testid="ofci-project-destination">
          <div className="flex flex-wrap items-center gap-3 text-[12px]">
            <label className="flex items-center gap-1.5">
              <input type="radio" name="ofci-dest" checked={dest === "location"} onChange={() => setDest("location")}
                     data-testid="ofci-dest-location" /> Found at one storage location
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" name="ofci-dest" checked={dest === "assigned"} onChange={() => setDest("assigned")}
                     data-testid="ofci-dest-assigned" /> Spread straight to the assigned rooms
            </label>
            <label className="flex items-center gap-1 ml-auto">Date
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                     className="w-40 min-h-[40px] sm:min-h-[32px] text-[13px]" data-testid="ofci-project-date" />
            </label>
          </div>
          {dest === "location" && (
            <div className="rounded border border-gray-200 p-2 space-y-1" data-testid="ofci-project-location">
              <DropLocationPicker projectId={projectId} value={loc} onChange={setLoc} />
              <Input placeholder="or describe the spot — e.g. loading dock, storage 114, north stair"
                     value={loc.note} className="min-h-[40px] sm:min-h-[34px] text-[13px]" data-testid="ofci-project-label"
                     onChange={(e) => setLoc((c) => ({ ...c, note: e.target.value }))} />
              <div className="text-[11px] text-gray-500">
                Material stays at this location until someone moves it — its FRS room assignment is kept separately.
              </div>
            </div>
          )}
          <Input placeholder="Note (optional) — what the owner or GC said, pallet marks, damage" value={note}
                 className="min-h-[40px] sm:min-h-[34px] text-[13px]" data-testid="ofci-project-note"
                 onChange={(e) => setNote(e.target.value)} />
        </div>
        <DialogFooter className="px-4 py-3 border-t border-gray-200 gap-2 shrink-0 flex-wrap">
          {problems.length > 0 && (
            <div className="basis-full text-[12px] text-red-700" data-testid="ofci-project-problems">
              {problems[0]}{problems.length > 1 ? ` (+${problems.length - 1} more)` : ""}
            </div>
          )}
          <span className="text-[12px] text-gray-600 mr-auto" data-testid="ofci-project-selected">
            <b>{chosen.length}</b> product(s) · <b>{units}</b> unit(s) selected · verified by <b>{actor || "—"}</b>
          </span>
          <Button variant="outline" onClick={onClose} className="min-h-[44px] sm:min-h-[36px]" data-testid="ofci-project-cancel">Close</Button>
          <Button onClick={submit} disabled={busy || !chosen.length || problems.length > 0}
                  className="min-h-[44px] sm:min-h-[36px]" data-testid="ofci-project-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save verification
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
